import type { LoginResponse, SessionUser } from "@xop/dashboard-shared";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";

import { ApiError, api } from "../lib/api.js";
import { onSessionLost } from "../lib/sessionEvents.js";
import { startSessionKeepAlive } from "../lib/sessionKeepAlive.js";
import { tokenStore } from "../lib/tokenStore.js";

interface AuthState {
  user: SessionUser | null;
  /**
   * "loading" until the first /me roundtrip completes; "ready" after.
   *
   * "offline" is the third state, and the reason this session finally
   * stays put (docs/session-lifetime.md): we hold tokens, but the server
   * can't be reached to hydrate the user. That used to be indistinguishable
   * from "logged out" — the provider wiped the tokens on ANY /me failure,
   * so reloading during a deploy, on a flaky connection, or before the wifi
   * reconnected ended a session that had months left on it. Now the tokens
   * survive, `<RequireAuth>` shows a retry screen instead of /login, and a
   * backoff loop reconnects on its own.
   */
  status: "loading" | "ready" | "offline";
}

interface AuthApi extends AuthState {
  login: (input: LoginInput) => Promise<LoginOutcome>;
  logout: () => Promise<void>;
  refreshSession: () => Promise<void>;
  /** Mint a fresh access+refresh token pair and reload the session. Used
   * after 2FA enrollment: the server gate reads `must_enroll` from the
   * TOKEN, so a /me-only refresh wouldn't clear it — we must re-issue the
   * token (which the server recomputes as enrolled). */
  reauthenticate: () => Promise<void>;
}

export interface LoginInput {
  email: string;
  password: string;
  otp?: string;
}

export type LoginOutcome =
  | { kind: "ok"; user: SessionUser }
  | {
      kind: "two_factor_required";
      userId: number;
      /** Which second factor to prompt for — drives the challenge copy. */
      method: "totp" | "email";
      /** For the email method, the address the code was just emailed to. */
      emailHint: string | null;
    }
  | { kind: "error"; message: string; code?: string };

const ctx = createContext<AuthApi | null>(null);

export type LoginBody =
  | {
      twoFactor: true;
      userId: number;
      method: "totp" | "email";
      emailHint: string | null;
    }
  | { twoFactor: false; data: LoginResponse };

/**
 * Interpret the `/api/auth/login` response. Pure + exported so the
 * branching — including the defensive non-object guard — is unit-tested
 * without rendering the whole provider.
 *
 * A non-object body means the request never reached the dashboard API
 * (e.g. a desktop build pointed at the wrong server gets its own bundled
 * HTML back). Fail with a clear message instead of a cryptic
 * `Cannot use 'in' operator to search for 'two_factor_required' …` crash.
 */
export function interpretLoginBody(body: unknown): LoginBody {
  if (!body || typeof body !== "object") {
    throw new Error(
      "Couldn't reach the XOP server — check the app's server URL and your connection.",
    );
  }
  if ("two_factor_required" in body) {
    const b = body as Record<string, unknown>;
    return {
      twoFactor: true,
      userId: b.user_id as number,
      method: b.two_factor_method === "email" ? "email" : "totp",
      emailHint: typeof b.email_hint === "string" ? b.email_hint : null,
    };
  }
  return { twoFactor: false, data: body as LoginResponse };
}

/** Backoff ladder for re-hydrating the session after a transient failure. */
const HYDRATE_RETRY_MS = [3_000, 8_000, 20_000, 60_000];

/**
 * Did this `/me` failure mean the session is over, or just that we
 * couldn't reach the server?
 *
 * `lib/api.ts` already did the hard part: on a 401 it tries a refresh and,
 * if (and ONLY if) the refresh token was rejected, clears the token store
 * and fires session-lost. So an empty token store IS the dead signal, and
 * it is the only one we trust here.
 *
 * In particular a 401 with the tokens still in place does NOT end the
 * session: it means the refresh couldn't complete (offline, 5xx, the 503
 * the server returns when portal-api is unreachable). Treating that 401 as
 * "logged out" would reinstate the exact bug this file is fixing, just one
 * layer up. Retry instead.
 */
export function isSessionEnded(hasRefreshToken: boolean): boolean {
  return !hasRefreshToken;
}

export function AuthProvider({ children }: { children: ReactNode }): JSX.Element {
  const [state, setState] = useState<AuthState>({ user: null, status: "loading" });
  const retryTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const retryIndex = useRef(0);

  const clearRetry = useCallback(() => {
    if (retryTimer.current !== null) {
      clearTimeout(retryTimer.current);
      retryTimer.current = null;
    }
  }, []);

  const refreshSession = useCallback(async () => {
    clearRetry();
    // No stored session at all → genuinely signed out. A missing ACCESS
    // token alone isn't enough: the refresh chain can mint a new one, and
    // api() does exactly that on the 401.
    if (!tokenStore.getAccess() && !tokenStore.getRefresh()) {
      setState({ user: null, status: "ready" });
      return;
    }
    try {
      const me = await api<SessionUser>("/api/auth/me");
      retryIndex.current = 0;
      setState({ user: me, status: "ready" });
    } catch {
      if (isSessionEnded(Boolean(tokenStore.getRefresh()))) {
        tokenStore.clear();
        retryIndex.current = 0;
        setState({ user: null, status: "ready" });
        return;
      }
      // Transient — keep the tokens and keep trying. An already-hydrated
      // user stays on screen (the app degrades per-request instead of
      // yanking the whole UI away).
      setState((prev) => (prev.user ? prev : { user: null, status: "offline" }));
      const wait =
        HYDRATE_RETRY_MS[Math.min(retryIndex.current, HYDRATE_RETRY_MS.length - 1)]!;
      retryIndex.current += 1;
      retryTimer.current = setTimeout(() => {
        retryTimer.current = null;
        void refreshSession();
      }, wait);
    }
  }, [clearRetry]);

  useEffect(() => {
    void refreshSession();
    return clearRetry;
  }, [refreshSession, clearRetry]);

  // Renew the access token BEFORE it expires (and on wake / reconnect), so
  // the session doesn't depend on a 401 landing at a moment when the
  // network happens to work. See lib/sessionKeepAlive.ts.
  useEffect(() => startSessionKeepAlive(), []);

  // Global session-lost signal — fired by lib/api.ts and crm/lib/api.ts
  // when an API call comes back 401 and refresh has been exhausted.
  // We clear tokens and drop `user` so every <RequireAuth> gate
  // re-renders into a redirect on its next pass.
  useEffect(
    () =>
      onSessionLost(() => {
        tokenStore.clear();
        setState({ user: null, status: "ready" });
      }),
    [],
  );

  const login = useCallback<AuthApi["login"]>(async (input) => {
    try {
      const body = await api<unknown>("/api/auth/login", {
        method: "POST",
        body: input,
        anonymous: true,
        retryOnAuthError: false,
      });
      const parsed = interpretLoginBody(body);
      if (parsed.twoFactor) {
        return {
          kind: "two_factor_required",
          userId: parsed.userId,
          method: parsed.method,
          emailHint: parsed.emailHint,
        };
      }
      tokenStore.set(parsed.data.access_token, parsed.data.refresh_token);
      setState({ user: parsed.data.user, status: "ready" });
      return { kind: "ok", user: parsed.data.user };
    } catch (err) {
      const message =
        err instanceof ApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : "Login failed";
      return {
        kind: "error",
        message,
        ...(err instanceof ApiError && err.code ? { code: err.code } : {}),
      };
    }
  }, []);

  const logout = useCallback<AuthApi["logout"]>(async () => {
    await api("/api/auth/logout", { method: "DELETE" }).catch(() => {});
    tokenStore.clear();
    setState({ user: null, status: "ready" });
  }, []);

  const reauthenticate = useCallback<AuthApi["reauthenticate"]>(async () => {
    const refresh = tokenStore.getRefresh();
    if (!refresh) {
      setState({ user: null, status: "ready" });
      return;
    }
    const body = await api<LoginResponse>("/api/auth/refresh", {
      method: "POST",
      body: { refresh_token: refresh },
      anonymous: true,
      retryOnAuthError: false,
    });
    tokenStore.set(body.access_token, body.refresh_token);
    setState({ user: body.user, status: "ready" });
  }, []);

  const value = useMemo<AuthApi>(
    () => ({ ...state, login, logout, refreshSession, reauthenticate }),
    [state, login, logout, refreshSession, reauthenticate],
  );

  return <ctx.Provider value={value}>{children}</ctx.Provider>;
}

export function useAuth(): AuthApi {
  const v = useContext(ctx);
  if (!v) throw new Error("useAuth must be used within AuthProvider");
  return v;
}

/** Non-throwing variant — returns null when there is no `<AuthProvider>` above.
 * For drop-in widgets that may render in isolation (e.g. a page unit test that
 * mounts a single page without the app shell) and must degrade to "no user"
 * instead of crashing the tree. */
export function useAuthOptional(): AuthApi | null {
  return useContext(ctx);
}

export function useSessionUser(): SessionUser {
  const { user } = useAuth();
  if (!user) {
    throw new Error(
      "useSessionUser called outside an authenticated route — wrap with <RequireAuth>",
    );
  }
  return user;
}
